import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

interface DailyStreak {
  id: string;
  user_id: string;
  current_streak: number;
  longest_streak: number;
  last_claim_date: string | null;
  total_bonuses_claimed: number;
  created_at: string;
  updated_at: string;
}

interface DailyBonusState {
  loading: boolean;
  canClaim: boolean;
  currentStreak: number;
  longestStreak: number;
  todayReward: { coins: number; gems: number };
  streakDay: number; // 1-7
  lastClaimDate: string | null;
  totalClaimed: number;
}

// Options for the hook - currency functions passed from parent
interface UseDailyBonusOptions {
  awardCurrency: (coins: number, gems: number, reason: string, sourceData?: any) => Promise<any>;
  refreshCurrency: () => Promise<void>;
}

// Calculate reward based on streak day (1-7 cycle)
const calculateReward = (streakDay: number) => {
  const baseCoins = 25;
  const multipliers = [1, 1.2, 1.4, 1.6, 1.8, 2.0, 2.5];
  const dayIndex = ((streakDay - 1) % 7); // 0-6
  
  return {
    coins: Math.floor(baseCoins * multipliers[dayIndex]),
    gems: dayIndex === 6 ? 3 : 0 // Gems on day 7
  };
};

// Calculate new streak based on last claim date
const calculateNewStreak = (lastClaimDate: string | null, currentStreak: number): number => {
  if (!lastClaimDate) return 1;
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const lastClaim = new Date(lastClaimDate);
  lastClaim.setHours(0, 0, 0, 0);
  
  const daysDiff = Math.floor((today.getTime() - lastClaim.getTime()) / (1000 * 60 * 60 * 24));
  
  if (daysDiff === 1) {
    // Consecutive day - increment streak
    return currentStreak + 1;
  } else if (daysDiff > 1) {
    // Missed days - reset to 1
    return 1;
  }
  
  // Same day (already claimed)
  return currentStreak;
};

export const useDailyBonus = ({ awardCurrency, refreshCurrency }: UseDailyBonusOptions) => {
  const { user } = useAuth();
  
  const [state, setState] = useState<DailyBonusState>({
    loading: true,
    canClaim: false,
    currentStreak: 0,
    longestStreak: 0,
    todayReward: { coins: 0, gems: 0 },
    streakDay: 1,
    lastClaimDate: null,
    totalClaimed: 0,
  });

  const [claiming, setClaiming] = useState(false);

  // Fetch or create streak data
  const fetchStreakData = async () => {
    if (!user) {
      setState(prev => ({ ...prev, loading: false }));
      return;
    }

    try {
      const { data: streakData, error } = await supabase
        .from('daily_login_streaks')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching streak data:', error);
        setState(prev => ({ ...prev, loading: false }));
        return;
      }

      const today = new Date().toISOString().split('T')[0];

      if (!streakData) {
        // First time user - create streak record
        setState({
          loading: false,
          canClaim: true,
          currentStreak: 0, // Will become 1 on claim
          longestStreak: 0,
          todayReward: calculateReward(1),
          streakDay: 1,
          lastClaimDate: null,
          totalClaimed: 0,
        });
        return;
      }

      // Check if already claimed today
      const alreadyClaimed = streakData.last_claim_date === today;
      
      if (alreadyClaimed) {
        // Already claimed today
        const streakDay = ((streakData.current_streak - 1) % 7) + 1;
        setState({
          loading: false,
          canClaim: false,
          currentStreak: streakData.current_streak,
          longestStreak: streakData.longest_streak,
          todayReward: calculateReward(streakDay),
          streakDay,
          lastClaimDate: streakData.last_claim_date,
          totalClaimed: streakData.total_bonuses_claimed,
        });
      } else {
        // Can claim - calculate what the new streak will be
        const newStreak = calculateNewStreak(streakData.last_claim_date, streakData.current_streak);
        const streakDay = ((newStreak - 1) % 7) + 1;
        
        setState({
          loading: false,
          canClaim: true,
          currentStreak: streakData.current_streak,
          longestStreak: streakData.longest_streak,
          todayReward: calculateReward(streakDay),
          streakDay,
          lastClaimDate: streakData.last_claim_date,
          totalClaimed: streakData.total_bonuses_claimed,
        });
      }
    } catch (error) {
      console.error('Error in fetchStreakData:', error);
      setState(prev => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    fetchStreakData();
  }, [user]);

  const claimBonus = async () => {
    if (!user || !state.canClaim || claiming) return;

    setClaiming(true);

    try {
      const today = new Date().toISOString().split('T')[0];
      const newStreak = calculateNewStreak(state.lastClaimDate, state.currentStreak);
      const newLongestStreak = Math.max(newStreak, state.longestStreak);

      // Upsert streak data
      const { error: streakError } = await supabase
        .from('daily_login_streaks')
        .upsert({
          user_id: user.id,
          current_streak: newStreak,
          longest_streak: newLongestStreak,
          last_claim_date: today,
          total_bonuses_claimed: state.totalClaimed + 1,
        }, {
          onConflict: 'user_id'
        });

      if (streakError) {
        console.error('Error updating streak:', streakError);
        toast.error('Failed to update streak');
        setClaiming(false);
        return;
      }

      // Award currency
      const result = await awardCurrency(
        state.todayReward.coins,
        state.todayReward.gems,
        'daily_bonus',
        {
          streak_day: state.streakDay,
          current_streak: newStreak,
        }
      );

      if (result) {
        // Refresh currency display
        await refreshCurrency();
        
        // Update state
        setState(prev => ({
          ...prev,
          canClaim: false,
          currentStreak: newStreak,
          longestStreak: newLongestStreak,
          lastClaimDate: today,
          totalClaimed: prev.totalClaimed + 1,
        }));

        toast.success(
          `🎁 Daily bonus claimed! ${state.todayReward.coins} coins${state.todayReward.gems > 0 ? ` + ${state.todayReward.gems} gems` : ''}`,
          { duration: 4000 }
        );
      } else {
        toast.error('Failed to claim daily bonus');
      }
    } catch (error) {
      console.error('Error claiming bonus:', error);
      toast.error('Failed to claim daily bonus');
    } finally {
      setClaiming(false);
    }
  };

  return {
    ...state,
    claiming,
    claimBonus,
    refreshStreakData: fetchStreakData,
  };
};
